import { useRef, useState } from "react";
import { X } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * CCChipsInput — chip-style multi-email input.
 * props:
 *   value: string[]  (current chips)
 *   onChange(next: string[])
 *   suggestions: string[]  (for datalist)
 *   max: number  (default 5)
 *   testId: string
 */
export default function CCChipsInput({
    value = [],
    onChange,
    suggestions = [],
    max = 5,
    testId = "cc-chips",
}) {
    const [text, setText] = useState("");
    const [error, setError] = useState("");
    const [shake, setShake] = useState(false);
    const inputRef = useRef(null);
    const full = value.length >= max;

    const listId = `${testId}-datalist`;

    const commit = (raw) => {
        const v = (raw || "").trim().replace(/,+$/, "");
        if (!v) return true;
        if (!EMAIL_RE.test(v)) {
            setError("Invalid email");
            setShake(true);
            setTimeout(() => setShake(false), 250);
            return false;
        }
        // Dedupe case-insensitive
        if (value.some((e) => e.toLowerCase() === v.toLowerCase())) {
            setText("");
            setError("");
            return true;
        }
        if (value.length >= max) return false;
        onChange([...value, v]);
        setText("");
        setError("");
        return true;
    };

    const removeAt = (idx) => {
        const next = value.filter((_, i) => i !== idx);
        onChange(next);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(text);
        } else if (e.key === "Backspace" && !text && value.length) {
            e.preventDefault();
            removeAt(value.length - 1);
        }
    };

    const onBlur = () => {
        if (text.trim()) commit(text);
    };

    return (
        <div data-testid={testId}>
            <datalist id={listId}>
                {(suggestions || []).map((e) => <option key={e} value={e} />)}
            </datalist>
            <div
                className={`input flex flex-wrap items-center gap-1.5 min-h-[48px] py-1.5 ${
                    shake ? "field-shake" : ""
                }`}
                onClick={() => inputRef.current?.focus()}
            >
                {value.map((email, i) => (
                    <span
                        key={i}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[12px] text-emerald-800 h-[32px]"
                        data-testid={`${testId}-chip-${i}`}
                    >
                        <span className="max-w-[220px] truncate">{email}</span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                            className="text-emerald-700 hover:text-red-600"
                            aria-label={`Remove ${email}`}
                            data-testid={`${testId}-chip-remove-${i}`}
                        >
                            <X size={12} strokeWidth={2.5} />
                        </button>
                    </span>
                ))}
                {!full && (
                    <input
                        ref={inputRef}
                        list={listId}
                        type="email"
                        className="flex-1 bg-transparent outline-none border-0 min-w-[120px] text-[14px] px-1 py-1"
                        value={text}
                        onChange={(e) => { setText(e.target.value); if (error) setError(""); }}
                        onKeyDown={onKeyDown}
                        onBlur={onBlur}
                        placeholder={value.length ? "" : "email@example.com"}
                        autoComplete="off"
                        data-testid={`${testId}-input`}
                    />
                )}
            </div>
            {full ? (
                <div className="text-[12px] text-slate-400 mt-1" data-testid={`${testId}-max-note`}>
                    Max {max} CC recipients
                </div>
            ) : (
                <div className="text-[12px] text-slate-400 mt-1">
                    Copies these people on the certified report email. Optional.
                </div>
            )}
            {error && (
                <div className="err mt-1" data-testid={`${testId}-error`}>{error}</div>
            )}
        </div>
    );
}
